import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useNative } from "../../../shared/phone/media";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { type AdFormat, type AdPost } from "../../../shared/goals/ready";
import type { GoalConfig } from "../../../shared/goals/config";
import { metaSettings } from "../../../shared/goals/meta";
import { planIdeas } from "../../../shared/goals/plan";
import { Icon } from "../../components/ui/Icon";
import { api, ApiError } from "../../lib/api";
import { ccApi } from "../../lib/ccApi";
import { goalsApi, type GoalView } from "../../lib/goalsApi";
import { hideProvider } from "../../lib/connectionsApi";
import { drafting as runDrafting, readyApi, stopped as runStopped, type LaunchView, type ReadyAd, type ReadyView } from "../../lib/readyApi";
import { FlowHead, type ProductRef } from "../goal/FlowHead";
import { META_STEPS } from "../goal/steps";
import { longDay } from "../goal/text";
import { useToast } from "../goal/useToast";
import { AdItem, IDEA_FORMATS } from "./AdItem";
import { AdsNative } from "./AdsNative";
import { clockTime, launchState, notDone, OPEN } from "./LaunchFoot";
import { PostPicker } from "./PostPicker";
import { WhereOpens } from "./WhereOpens";
import { useCan } from "../../context/AuthContext";
import "../../styles/wizard.css";
import "../../styles/ads.css";

/**
 * The Ads step (notes/AUDIENCE.md 5.6, 5.10, 5.11): where the ads open, the owner's own ads first (a video
 * they have, or an idea in a line), the plan's ideas as suggestions, and Launch at the bottom: the one yes,
 * which approves exactly what this page shows.
 */

const POLL_MS = 5000;

const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const host = (url: string | undefined) => {
  try {
    return url ? new URL(url).host : "";
  } catch {
    return "";
  }
};

/** The page as people read it: its address without https://, so the owner sees which page, not only which site. */
const pageOf = (url: string | undefined) => {
  try {
    if (!url) return "";
    const u = new URL(url);
    return `${u.host}${u.pathname.replace(/\/$/, "")}${u.search}`;
  } catch {
    return "";
  }
};

/** The config with these Meta settings changed, for a new version. */
const withSettings = (c: GoalConfig, patch: Record<string, unknown>): GoalConfig => ({
  ...c,
  channels: { ...c.channels, meta: { ...c.channels.meta!, settings: { ...c.channels.meta!.settings, ...patch } } },
});

function useProduct(productId: string | null) {
  const [product, setProduct] = useState<ProductRef | null>(null);
  useEffect(() => {
    if (!productId) return;
    api.products.list().then(
      (r) => {
        const p = r.products.find((x) => x.id === productId);
        setProduct(p ? { id: p.id, name: p.name, logoR2Key: p.logoR2Key, coldEmail: p.coldEmail } : null);
      },
      () => setProduct(null),
    );
  }, [productId]);
  return product;
}

/** The plan ideas the owner skipped, kept on this device. */
function useSkipped(goalId: string): [string[], (keys: string[]) => void] {
  const key = `fadz.skipped.${goalId}`;
  const [skipped, setSkipped] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(key) ?? "[]") as string[];
    } catch {
      return [];
    }
  });
  const set = (keys: string[]) => {
    setSkipped(keys);
    try {
      localStorage.setItem(key, JSON.stringify(keys));
    } catch {
      // Why: a browser that keeps nothing only forgets the skips on reload.
    }
  };
  return [skipped, set];
}

export function AdsStep() {
  const { goalId = "" } = useParams();
  const navigate = useNavigate();
  const native = useNative();
  // Why: the code a laptop shows opens this step on the phone with the ad to record.
  const [params, setParams] = useSearchParams();
  const recordFor = params.get("record");
  const { toast, say } = useToast();
  const [view, setView] = useState<GoalView | null>(null);
  const [ready, setReady] = useState<ReadyView | null>(null);
  const [launch, setLaunch] = useState<LaunchView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [idea, setIdea] = useState("");
  const [fmt, setFmt] = useState<AdFormat>("camera");
  const [note, setNote] = useState("");
  const [noteMiss, setNoteMiss] = useState(false);
  const noteRef = useRef<HTMLInputElement>(null);
  /** Open the file chooser, or say what is missing first: the agent writes the words from that line. */
  const choose = (input: React.RefObject<HTMLInputElement | null>) => {
    if (!note.trim()) {
      setNoteMiss(true);
      noteRef.current?.focus();
      return;
    }
    input.current?.click();
  };
  const [busy, setBusy] = useState(false);
  const may = useCan()("admin");
  const [link, setLink] = useState("");
  const [daily, setDaily] = useState("");
  const [skipped, setSkipped] = useSkipped(goalId);
  const [removed, setRemoved] = useState<string | null>(null);
  const undoTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(undoTimer.current), []);
  const ownFile = useRef<HTMLInputElement>(null);
  const ownPhotos = useRef<HTMLInputElement>(null);
  /** The post picker: closed, open for a new ad ("new"), or open to change the post of one ad. */
  const [picking, setPicking] = useState<ReadyAd | "new" | null>(null);
  const version = useRef(0);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  /** The config the next save builds on: the last one loaded or saved. */
  const saving = useRef<GoalConfig | null>(null);

  const load = useCallback(async () => {
    try {
      const [v, r, l] = await Promise.all([goalsApi.get(goalId), readyApi.get(goalId), readyApi.launch(goalId)]);
      setView(v);
      setReady(r);
      setLaunch(l);
      version.current = v.goal.version;
      saving.current = v.current?.config ?? null;
      const s = metaSettings(v.current?.config.channels.meta?.settings);
      setLink((x) => x || (s.link ?? ""));
      const cents = s.dailyCents ?? v.current?.config.money.dailyMaxCents ?? null;
      setDaily((x) => x || (cents ? String(Math.round(cents / 100)) : ""));
      setError(null);
    } catch (e) {
      setError(message(e));
    }
  }, [goalId]);
  useEffect(() => {
    void load();
  }, [load]);

  const writing = !!ready && (ready.ads.some((a) => a.status === "drafting") || runDrafting(ready.runs.ads));
  const pending = !!launch && (launch.actions.some((a) => OPEN.includes(a.state)) || launch.campaign?.state === "creating");
  useEffect(() => {
    if (!writing && !pending) return;
    const t = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(t);
  }, [writing, pending, load]);

  const product = useProduct(view?.goal.productId ?? null);
  const config = view?.current?.config ?? null;
  const ideas = useMemo(() => (view?.current?.plan ? planIdeas(view.current.plan) : []), [view]);

  if (native && (!view || !ready || !launch || !config))
    return (
      <div className="pn-screen ad-scr">
        <PhoneNav title="Ads" large back={{ to: `/goals/${goalId}`, label: "Goal" }} />
        <p className="pn-empty">{error && !view ? error : "Loading the ads…"}</p>
      </div>
    );
  if (error && !view) return <div className="wrap ga gw ad"><p className="load-note">{error}</p></div>;
  if (!view || !ready || !launch || !config) return <div className="wrap ga gw ad"><p className="load-note">Loading the ads…</p></div>;

  const settings = metaSettings(config.channels.meta?.settings);
  const opens = settings.opens === "metaform" ? "Meta's form" : host(settings.link);
  const opensPage = settings.opens === "metaform" ? "Meta's form" : pageOf(settings.link);
  const ads = ready.ads.filter((a) => a.status !== "launched");
  const used = new Set(ready.ads.map((a) => a.ideaKey));
  const open = ideas.filter((x) => !used.has(x.key) && !skipped.includes(x.key));
  const gone = ideas.filter((x) => !used.has(x.key) && skipped.includes(x.key));
  const noVideo = ads.reduce((n, a) => n + a.missing, 0);
  const campaign = launch.campaign;

  const act = async (f: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try {
      await f();
      if (ok) say(ok);
      await load();
    } catch (e) {
      say(message(e), false);
      await load();
    } finally {
      setBusy(false);
    }
  };
  /** A setting changed on this page becomes the goal's next version, one change at a time, each on the last. */
  const saveSettings = (patch: Record<string, unknown>, summary: string) => {
    const run = queue.current.then(() =>
      act(async () => {
        const next = withSettings(saving.current ?? config, patch);
        const r = await ccApi.apply(goalId, version.current, next, summary);
        version.current = r.version;
        saving.current = next;
      }),
    );
    queue.current = run;
    return run;
  };
  const saveLink = () => {
    const v = link.trim();
    if (v === (settings.link ?? "")) return;
    if (!/^https:\/\/[^\s/]+\.[^\s]+/.test(v)) return say("The page address starts with https://", false);
    void saveSettings({ link: v }, "Where the ads open: the page changed");
  };
  const saveDaily = () => {
    const n = Math.round(Number(daily.replace(/[^0-9.]/g, "")) * 100);
    if (!Number.isFinite(n) || n <= 0 || n === settings.dailyCents) return;
    void saveSettings({ dailyCents: n }, `Meta's daily budget: $${(n / 100).toLocaleString("en-US")}`);
  };
  const addOwn = (f: File) => {
    if (!note.trim()) return say("Say in a line what it shows first.", false);
    void act(async () => {
      await readyApi.addVideo(goalId, f, note.trim());
      setNote("");
    });
  };
  const addPhotos = (files: File[]) => {
    if (!note.trim()) return say("Say in a line what it shows first.", false);
    if (files.length < 2 || files.length > 10) return say("Choose 2 to 10 photos.", false);
    void act(async () => {
      await readyApi.addPhotos(goalId, files, note.trim());
      setNote("");
    });
  };
  const pickPost = (p: AdPost) => {
    const target = picking;
    setPicking(null);
    void act(
      () => (target && target !== "new" ? readyApi.setPost(goalId, target.id, target.rev, p.id) : readyApi.addPost(goalId, p.id)),
      target && target !== "new" ? undefined : "Added. Meta keeps the post's own words.",
    );
  };
  const { create, turnOn, creating, switching, wait } = launchState(launch);
  const removedAd = (adId: string) => {
    setRemoved(adId);
    clearTimeout(undoTimer.current);
    undoTimer.current = setTimeout(() => setRemoved(null), 5000);
  };
  const undoRemove = () => {
    const id = removed;
    setRemoved(null);
    if (id) void act(() => readyApi.restoreIdea(goalId, id), "The ad is back.");
  };
  const pressLaunch = () =>
    void act(async () => {
      await readyApi.launchNow(goalId, { version: version.current, ads: Object.fromEntries(ads.filter((a) => a.status === "ready").map((a) => [a.id, a.rev])) });
      navigate(`/goals/${goalId}`);
    });

  if (native)
    return (
      <AdsNative
        goalId={goalId}
        view={view}
        ready={ready}
        launch={launch}
        config={config}
        settings={settings}
        product={product}
        opens={opens}
        opensPage={opensPage}
        ads={ads}
        open={open}
        gone={gone}
        skipped={skipped}
        setSkipped={setSkipped}
        busy={busy}
        act={act}
        saveSettings={saveSettings}
        link={link}
        setLink={setLink}
        saveLink={saveLink}
        note={note}
        setNote={setNote}
        addOwn={addOwn}
        addPhotos={addPhotos}
        picking={picking}
        setPicking={setPicking}
        pickPost={pickPost}
        removed={removed}
        removedAd={removedAd}
        undoRemove={undoRemove}
        pressLaunch={pressLaunch}
        load={load}
        recordFor={recordFor}
        say={say}
        toast={toast}
      />
    );

  return (
    <div className="wrap ga gw ad">
      <FlowHead
        back={{ to: `/goals/${goalId}`, label: "Goal" }}
        crumb="New goal · Meta ads"
        product={product}
        steps={META_STEPS}
        step={6}
        canGo={(i) => i >= 4}
        onGo={(i) => navigate(i === 4 ? `/goals/${goalId}/plan` : i === 5 ? `/goals/${goalId}/audience` : `/goals/${goalId}/ads`)}
        title="Ads"
        goalRef={view.goal.sentence}
      />

      <WhereOpens
        goalId={goalId}
        settings={settings}
        suggested={view.current?.plan?.meta_events?.learns_from ?? null}
        suggestedOpens={view.current?.plan?.meta_opens?.opens ?? null}
        link={link}
        setLink={setLink}
        saveLink={saveLink}
        saveSettings={saveSettings}
        say={say}
      />

      <section className="card" id="y-ads" style={{ marginTop: 18 }}>
        <div className="ysec-h">
          <h2>Your ads</h2>
          <span className={`pill ${ads.length && !noVideo ? "good" : "tight"}`}>{ads.length ? (noVideo ? `${noVideo} file${noVideo === 1 ? "" : "s"} to add` : `${ads.length} ready`) : "None yet"}</span>
        </div>
        <p className="hint" style={{ marginBottom: 14 }}>
          3 to 5 different ads work best.
        </p>
        <div className="ways">
          <div className="way">
            <span className="ki">
              <Icon name="film" />
            </span>
            <span className="own-b">
              <b>A video or photos you already have</b>
              <p>The agent writes the words. Two or more photos make one ad people swipe through.</p>
              <input
                ref={noteRef}
                className="own-note"
                value={note}
                placeholder="What it shows, in a line"
                aria-label="What it shows"
                aria-invalid={noteMiss}
                onChange={(e) => {
                  setNote(e.target.value);
                  setNoteMiss(false);
                }}
              />
              {noteMiss && <p className="warnl">Say what it shows first, in a line.</p>}
              <input
                ref={ownFile}
                type="file"
                hidden
                accept="video/mp4,video/quicktime,image/jpeg,image/png,image/webp"
                aria-label="A video or photo"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) addOwn(f);
                }}
              />
              <input
                ref={ownPhotos}
                type="file"
                hidden
                multiple
                accept="image/jpeg,image/png,image/webp"
                aria-label="Photos"
                onChange={(e) => {
                  const fs = [...(e.target.files ?? [])];
                  e.target.value = "";
                  if (fs.length) addPhotos(fs);
                }}
              />
              <span className="gl-opts">
                <button className="btn sm" type="button" disabled={busy} onClick={() => choose(ownFile)}>
                  <Icon name="upload" />
                  Choose a video or photo
                </button>
                <button className="btn sm ghost" type="button" disabled={busy} onClick={() => choose(ownPhotos)}>
                  <Icon name="upload" />
                  Choose 2 to 10 photos
                </button>
              </span>
            </span>
          </div>
          <div className="way">
            <span className="ki">
              <Icon name="play" />
            </span>
            <span className="own-b">
              <b>A post you already published</b>
              <p>Its likes and comments come with it.</p>
              <button className="btn sm" type="button" disabled={busy} onClick={() => setPicking("new")}>
                Pick a post
              </button>
            </span>
          </div>
          <div className="way ownidea">
            <span className="ki">
              <Icon name="spark" />
            </span>
            <div className="own-b">
              <b>Your own idea</b>
              <input value={idea} placeholder="One line, for example: the question clients ask most, answered on camera" aria-label="Your own idea" onChange={(e) => setIdea(e.target.value)} />
              <div className="chiprow">
                {IDEA_FORMATS.map(([k, l]) => (
                  <button key={k} type="button" className={`cb${fmt === k ? " on" : ""}`} aria-pressed={fmt === k} onClick={() => setFmt(k)}>
                    {l}
                  </button>
                ))}
              </div>
              <button
                className="btn sm"
                type="button"
                disabled={busy || !idea.trim()}
                onClick={() =>
                  void act(async () => {
                    await readyApi.addIdea(goalId, idea.trim(), fmt);
                    setIdea("");
                  })
                }
              >
                <Icon name="plus" />
                Add this ad
              </button>
            </div>
          </div>
        </div>
        {ads.length > 0 && (
          <div className="adlist">
            {ads.map((a) => (
              <AdItem
                key={`${a.id}:${a.rev}`}
                goalId={goalId}
                ad={a}
                product={product}
                opens={opens}
                opensPage={opensPage}
                stoppedWhy={runStopped(ready.runs.ads) ? hideProvider(ready.runs.ads?.error ?? "") : null}
                onChanged={load}
                onRemoved={removedAd}
                onDraftAgain={() => void act(() => readyApi.draftAgain(goalId, "ads"), "The agent is writing the words again.")}
                onPickPost={(x) => setPicking(x)}
                say={say}
                recordTall={a.id === recordFor}
                onRecordOpened={() => setParams({}, { replace: true })}
              />
            ))}
          </div>
        )}
      </section>

      {(open.length > 0 || gone.length > 0) && (
        <section className="card" style={{ marginTop: 18 }}>
          <div className="ysec-h">
            <h2>Ideas from the plan</h2>
            {open.length > 1 && (
              <button className="btn sm" type="button" disabled={busy} onClick={() => void act(async () => Promise.all(open.map((x) => readyApi.usePlanIdea(goalId, x.key))))}>
                Use all {open.length}
              </button>
            )}
          </div>
          {open.map((x) => (
            <div className="idea" key={x.key}>
              <div>
                <b>{x.text}</b>
                <p>{x.why}</p>
              </div>
              <div className="gl-opts">
                <button className="btn sm" type="button" disabled={busy} onClick={() => void act(() => readyApi.usePlanIdea(goalId, x.key))}>
                  Use it
                </button>
                <button className="btn sm ghost" type="button" onClick={() => setSkipped([...skipped, x.key])}>
                  Not this one
                </button>
              </div>
            </div>
          ))}
          {gone.length > 0 && (
            <div className="skipped hint">
              Skipped:
              {gone.map((x) => (
                <button key={x.key} className="btn ghost sm" type="button" onClick={() => setSkipped(skipped.filter((k) => k !== x.key))}>
                  {x.text}
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="card launchbar" id="y-launch">
        {campaign?.state === "on" ? (
          <div className="launched">
            <Icon name="check" />
            <span>Launched.</span>
            <Link className="btn sm" to={`/goals/${goalId}`}>
              Results
            </Link>
          </div>
        ) : creating ? (
          <div className="launched">
            <span className="spin" />
            <span>Sending your ads to Meta</span>
          </div>
        ) : campaign?.state === "unsure" ? (
          <div className="launched">
            <span className="spin" />
            <span>Meta gave an unclear answer. Flashadz looks again at {clockTime(campaign.settleUntil)}, before making anything new.</span>
          </div>
        ) : campaign ? (
          <div className="launched">
            {switching ? <span className="spin" /> : <Icon name="pause" />}
            <span>{switching ? "Switching the ads on" : (notDone(turnOn, "Switching the ads on") ?? "On Meta, paused.")}</span>
            {!switching && may && (
              <button className="btn sm" type="button" disabled={busy} onClick={() => void act(() => readyApi.turnOn(goalId))}>
                {turnOn ? "Try again" : "Turn it on"}
              </button>
            )}
          </div>
        ) : (
          <>
            <div>
              <b>Launch</b>
              <p>
                Starts {longDay(new Date())}, at{" "}
                <label className="daychip">
                  $
                  <input inputMode="numeric" value={daily} aria-label="Daily budget" size={Math.max(3, daily.length + 1)} onChange={(e) => setDaily(e.target.value)} onBlur={saveDaily} />
                </label>{" "}
                a day.
              </p>
            </div>
            <div className="lb-go">
              {notDone(create, "Launching") && <span className="hint warnl">{notDone(create, "Launching")}</span>}
              {wait && <span className="hint">{wait}</span>}
              {may && (
                <button className="btn" type="button" disabled={!!wait || busy || pending} onClick={pressLaunch}>
                  <Icon name="check" />
                  Launch
                </button>
              )}
            </div>
          </>
        )}
      </section>
      <PostPicker goalId={goalId} productName={product?.name ?? "Your Page"} open={picking !== null} onClose={() => setPicking(null)} onPick={pickPost} />
      {toast}
      <div className={removed ? "toast on" : "toast"} role="status" aria-live="polite" aria-hidden={!removed}>
        <span>Ad removed.</span>
        <button type="button" className="t-undo" onClick={undoRemove}>
          Undo
        </button>
      </div>
    </div>
  );
}
